import { Component, Input, OnInit, OnChanges, SimpleChanges } from "@angular/core";
import { combineLatest, Observable, of, take } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";

import { OrderDetailStore } from "../../+state/order-detail.store";
import { OrderOrganizerAction } from "./actions/order-organizer-action";
import { OrderOrganizerEditAction } from "./actions/order-organizer-edit-action";
import { OrderOrganizerRemoveAction } from "./actions/order-organizer-remove-action";
import { OrderOrganizer, OrderOrganizerListItem } from "src/app/shared/models/order-organizer.model";
import { OrderOrganizerService } from "src/app/shared/services/order-organizer.service";
import { Order } from "src/app/shared/models/order";
import { ClientAccountInfoList } from "src/app/shared/models/client-account-info-list";
import { OrderOrganizerFormModalComponent } from "../order-organizer-form-modal/order-organizer-form-modal.component";
import { AppDialogModule } from "src/app/shared/components/app-dialog/app-dialog.component";
import { AppSpinnerModule } from "src/app/shared/modules/app-spinner.module";
import { CapabilitiesService } from "src/app/shared/services/capabilities/capabilities.service";

@Component({
    selector: "app-order-organizer-list",
    templateUrl: "./order-organizer-list.component.html",
    styleUrls: ["./order-organizer-list.component.scss"],
    standalone: false
})
export class OrderOrganizerListComponent implements OnInit, OnChanges {
  private actionHandler: (ParticipantListUser) => void;

  constructor(
    public store: OrderDetailStore,
    private readonly orderOrganizerService: OrderOrganizerService,
    private readonly modalService: NgbModal,
    private readonly dialog: AppDialogModule,
    private readonly spinner: AppSpinnerModule,
    private readonly toastr: ToastrService,
    private readonly capabilitiesService: CapabilitiesService
  ) { }

  @Input() showActions: boolean;
  @Input() orderId: number;

  isLoading = true;
  combinedOrganizers$: Observable<OrderOrganizerListItem[]>;

  sortColumn: string = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';

  ngOnInit() {
    this.combinedOrganizers$ = combineLatest([
      this.store.order$,
      this.store.organizers$,
      this.store.clientAccountInfoList$,
    ]).pipe(
      switchMap(([order, organizers, clientAccountInfoList]) =>
        this.combineOrganizers(order, organizers, clientAccountInfoList)
      )
    );
  }

  combineOrganizers(order: Order, organizers: OrderOrganizer[], clientAccountInfoList: ClientAccountInfoList): Observable<OrderOrganizerListItem[]> {

    // Create new array to mutate
    const newOrganizers = [...organizers];

    // Add 'CREATOR' item to organizers to show which user created the order
    if (order?.createdBy && clientAccountInfoList?.clients) {
      const createdByClientAccount = clientAccountInfoList.clients.find(x => x.accountId === order.createdBy);

      if (createdByClientAccount) {
        newOrganizers.unshift({
          id: createdByClientAccount.accountId,
          email: createdByClientAccount.emailAddress,
          firstName: createdByClientAccount.firstName,
          lastName: createdByClientAccount.lastName,
          role: 'CREATOR',
        });
      }
    }

    // If the order is locked, we don't need to add any actions to the list item,
    // so just convert each organizer to a list item
    if (!this.showActions) {
      return of(newOrganizers.map(organizer => {
        return {
          ...organizer,
          actions: []
        }
      }));
    }

    if (newOrganizers.length === 0) {
      return of([]); // ✅ avoids combineLatest([]) on empty
    }

    // The order is not locked, so we need to figure out the capabilities allowed on each order organizer.
    // Transform the order organizers into list items for the UI
    // During the transformation, also include the actions allowed on the list item based on capabilities
    return combineLatest(
      newOrganizers.map(organizer =>
        combineLatest([
          this.capabilitiesService.userHasCapability('organizer', 'CanEditOrganizer', organizer?.id),
          this.capabilitiesService.userHasCapability('organizer', 'CanRemoveOrganizer', organizer?.id)
        ])
          .pipe(
            map(([canEditOrganizer, canRemoveOrganizer]) => {
              const listItem: OrderOrganizerListItem = {
                ...organizer,
                actions: []
              };

              if (organizer.role === 'CREATOR') {
                return listItem;
              }

              if (canRemoveOrganizer) {
                listItem.actions.unshift(new OrderOrganizerRemoveAction(organizer, this.openRemoveOrderOrganizer));
              }

              if (canEditOrganizer) {
                listItem.actions.unshift(new OrderOrganizerEditAction(organizer, this.openEditOrderOrganizer));
              }

              return listItem;
            })
          )
      ));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.orderId?.currentValue) {
      this.isLoading = true;
      this.loadOrganizers();
    }
  }

  loadOrganizers(): void {
    combineLatest([
      this.orderOrganizerService.getOrderOrganizers(this.orderId),
      this.store.clientAccountInfoList$.pipe(
        filter(cal => Array.isArray(cal?.clients)),
        take(1)
      )
    ]).pipe(take(1)).subscribe(([organizers]) => {
      this.store.setOrganizers(organizers);
      this.isLoading = false;
    });
  }

  getFriendlyRole(organizer: OrderOrganizer): string {
    switch (organizer?.role) {
      case 'CREATOR':
        return 'Order Creator';
      default:
        return 'Order Manager';
    }
  }

  openEditOrderOrganizer(organizer: OrderOrganizer): void {
    const modalRef = this.modalService.open(OrderOrganizerFormModalComponent, {
      backdrop: "static",
      size: "lg",
    });

    modalRef.componentInstance.orderId = this.orderId;
    modalRef.componentInstance.selectedOrderOrganizer = organizer;
  }

  openRemoveOrderOrganizer(organizer: OrderOrganizer) {
    const name = `${organizer.firstName} ${organizer.lastName}`;
    this.dialog
      .show({
        title: "Delete Order Organizer",
        message:
          `Remove ${name} from the order?`,
        noButtonText: "Cancel",
        yesButtonText: "Delete Order Organizer",
      })
      .result.then((response) => {
        if (response === "yes") {
          this.removeOrganizer(organizer);
        }
      });
  }

  removeOrganizer(organizer: OrderOrganizer) {
    this.spinner.show();
    this.orderOrganizerService.deleteOrderOrganizer(organizer.id).subscribe(
      () => {
        this.loadOrganizers();
        this.capabilitiesService.getCapabilitiesByOrderId(this.orderId);
        this.toastr.success("Organizer removed.");
      },
      () => {
        this.toastr.error(`Unable to remove organizer`);
      }
    ).add(() => this.spinner.hide());
  }

  handleActionCall(action: OrderOrganizerAction) {
    this.actionHandler = action.action;
    this.actionHandler(action.organizer);
  }

  sortOrganizers(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.combinedOrganizers$.pipe(
      map(organizers => {
        return organizers.sort((a, b) => {
          const valueA = this.getColumnValue(a, column);
          const valueB = this.getColumnValue(b, column);

          if (valueA < valueB) {
            return this.sortDirection === 'asc' ? -1 : 1;
          } else if (valueA > valueB) {
            return this.sortDirection === 'asc' ? 1 : -1;
          } else {
            return 0;
          }
        });
      })
    ).subscribe(sortedOrganizers => {
      this.combinedOrganizers$ = of(sortedOrganizers);
    });
  }

  getColumnValue(item: OrderOrganizerListItem, column: string): any {
    switch (column) {
      case 'name':
        return `${item.firstName} ${item.lastName}`;
      case 'email':
        return item.email;
      case 'role':
        return this.getFriendlyRole(item);
      default:
        return '';
    }
  }

  getSortIcon(column: string): string {
    if (this.sortColumn === column) {
      return this.sortDirection === 'asc' ? 'assets/icons/arrow_up.svg' : 'assets/icons/arrow_down.svg';
    }
    return '';
  }
}
